import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./storeHooks";
import {
	deleteNotification,
	getNotificationCentreCountsThunk,
	getNotificationListThunk,
	markNotificationsAllRead,
	markReadNotification,
} from "../store/notifications/notificationThunk";
import {
	iNotificationFilter,
	iNotificationRowModel,
	NotificationListSurface,
} from "../types/notificationTypes";
import { toNotificationRowModel } from "../utils/notificationUtils";

export const useNotificationList = (surface: NotificationListSurface, limit: number) => {
	const dispatch = useAppDispatch();
	const list = useAppSelector((state) => state.notifications.lists[surface]);
	const counts = useAppSelector((state) => state.notifications.centreCounts);
	const rows = useMemo(() => list.items.map(toNotificationRowModel), [list.items]);

	const load = useCallback(
		(filter: iNotificationFilter) => {
			dispatch(getNotificationCentreCountsThunk());
			dispatch(getNotificationListThunk({ surface, filter, limit }));
		},
		[dispatch, surface, limit],
	);

	const changeFilter = useCallback(
		(filter: iNotificationFilter) => dispatch(getNotificationListThunk({ surface, filter, limit })),
		[dispatch, surface, limit],
	);

	const canLoadMore = !!list.nextCursor && list.status === "loaded";
	const loadMore = useCallback(() => {
		if (!canLoadMore) return;
		dispatch(getNotificationListThunk({ surface, filter: list.filter, cursor: list.nextCursor, limit }));
	}, [canLoadMore, dispatch, surface, list.filter, list.nextCursor, limit]);

	const markRead = useCallback(
		(row: iNotificationRowModel) => {
			if (!row.isRead) dispatch(markReadNotification(row.id));
		},
		[dispatch],
	);

	const remove = useCallback(
		(row: iNotificationRowModel) => {
			const notification = list.items.find((item) => item.id === row.id);
			if (notification) dispatch(deleteNotification(notification));
		},
		[dispatch, list.items],
	);

	const markAllRead = useCallback(() => dispatch(markNotificationsAllRead()), [dispatch]);

	return {
		filter: list.filter,
		status: list.status,
		rows,
		counts,
		unread: counts?.all?.unread ?? 0,
		canLoadMore,
		load,
		changeFilter,
		loadMore,
		markRead,
		remove,
		markAllRead,
	};
};

export const useLoadMoreSentinel = (onVisible: () => void, enabled: boolean) => {
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const onVisibleRef = useRef(onVisible);
	useEffect(() => {
		onVisibleRef.current = onVisible;
	});

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!enabled || !sentinel) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) onVisibleRef.current();
			},
			{ rootMargin: "200px 0px" },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [enabled]);

	return sentinelRef;
};
